import { skipToken } from "@reduxjs/toolkit/query";
import type { TestSignalProposalToolOutput } from "@repo/app-types/entities/configurationAgent";
import {
  copilotProposalIdSchema,
  type CopilotProposal,
} from "@repo/app-types/entities/copilotProposal";
import { defaultDateRange } from "@repo/app-types/entities/dateRange";
import type { PlaybookSelection } from "@repo/app-types/entities/playbook";
import {
  checkSignalHasUnpublishedChanges,
  type SignalId,
  type SignalMetadataSchema,
  type SignalPayload,
  type SignalVersion,
} from "@repo/app-types/entities/signal";
import type { SignalProposalData } from "@repo/app-types/entities/signalProposal";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { DateTime } from "luxon";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import {
  useGetPendingSignalProposalQuery,
  useResolveProposalMutation,
} from "../../../api/entities/copilotProposal.js";
import {
  useCreateSignalMutation,
  useGetSignalQuery,
  usePublishSignalMutation,
  useRevertSignalMutation,
  useSaveSignalDraftMutation,
  useTestSignalProposalMutation,
} from "../../../api/entities/signal.js";
import { appPaths } from "../../../app/appPaths.js";
import { useAutosaveDraft } from "../../../hooks/useAutosaveDraft.js";
import { useDraftSaveStatus } from "../../../hooks/useDraftSaveStatus.js";
import { useResearchAgentProps } from "../../researchAgent/researchAgentContext.js";
import { SignalEditorContainer } from "./signalEditorContainer.js";
import { SIGNAL_PROPOSAL_QUERY_PARAM } from "./signalEditorUrlState.js";
import { cleanMetadataSchema, isSignalFormDirty } from "./signalFormHelpers.js";
import {
  SignalTestConfigModal,
  type TestSignalFiltersUi,
} from "./signalTestConfigModal.js";
import { SignalTestMatchesModal } from "./signalTestMatchesModal.js";

type TestResultsState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error" }
  | {
      status: "complete";
      data: TestSignalProposalToolOutput;
      completedAt: DateTime;
    };

type FormFields = Pick<
  SignalVersion,
  "name" | "description" | "metadata_schema" | "playbook_scope"
>;

const EMPTY_FORM: FormFields = {
  name: "",
  description: "",
  metadata_schema: { fields: [] },
  playbook_scope: { type: "all" },
};

/**
 * The signal editor works on the signal's stored draft, like the playbook
 * editor on its draft revision: edits autosave to the draft, Publish makes it
 * live, Discard returns it to live. The cached signal is the base the form is
 * diffed against; every mutation replaces it. A new signal is local state
 * until its first autosave creates it, after which the URL moves to its id
 * and the editor remounts on the stored draft.
 */
export const SignalEditorPage: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { signalId } = useParams<{ signalId: SignalId }>();
  const isNew = !signalId;
  const navigate = useNavigate();

  // A new signal exists from its first save; the URL follows once its data
  // is in hand, so the editor remounts on it without its unmount save
  // re-sending the form.
  const [createdSignalId, setCreatedSignalId] = useState<SignalId | null>(null);
  // Mirrors `createdSignalId` for handlers that continue past the save they
  // awaited, before the state has rendered.
  const createdSignalIdRef = useRef<SignalId | null>(null);
  useEffect(() => {
    if (isNew && createdSignalId) {
      navigate(appPaths.settingsSignal.build({ signalId: createdSignalId }), {
        replace: true,
      });
    }
  }, [isNew, createdSignalId, navigate]);

  const {
    data: signal,
    isLoading: isLoadingSignal,
    isError,
  } = useGetSignalQuery(signalId ?? createdSignalId ?? skipToken);
  const [createSignal] = useCreateSignalMutation();
  const [saveSignalDraft] = useSaveSignalDraftMutation();
  const [publishSignal, { isLoading: isPublishing }] =
    usePublishSignalMutation();
  const [revertSignal, { isLoading: isReverting }] = useRevertSignalMutation();
  const [resolveProposal, { isLoading: isResolvingProposal }] =
    useResolveProposalMutation();
  const [testSignalProposal] = useTestSignalProposalMutation();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [metadataSchema, setMetadataSchema] = useState<SignalMetadataSchema>({
    fields: [],
  });
  const [playbookScope, setPlaybookScope] = useState<PlaybookSelection>({
    type: "all",
  });
  const [error, setError] = useState<string | null>(null);
  // The form is seeded from the stored draft once; later cache updates are
  // the form's own saves.
  const [isFormInitialized, setIsFormInitialized] = useState(isNew);

  const [testResultsState, setTestResultsState] = useState<TestResultsState>({
    status: "idle",
  });
  // Within-session memory of the filters used on the last actual run. When
  // non-null, the main Test signal click re-runs with these directly; otherwise
  // it opens the config modal so the user can pick.
  const [lastRunFilters, setLastRunFilters] =
    useState<TestSignalFiltersUi | null>(null);
  const [isTestConfigOpen, setIsTestConfigOpen] = useState(false);
  const [isMatchesModalOpen, setIsMatchesModalOpen] = useState(false);

  const setForm = useCallback((fields: FormFields) => {
    setName(fields.name);
    setDescription(fields.description);
    setMetadataSchema(fields.metadata_schema);
    setPlaybookScope(fields.playbook_scope);
  }, []);
  /** Set the form's fields that `data` proposes, from `source`. */
  const layProposalOverForm = useCallback(
    (data: SignalProposalData, source: Partial<FormFields>) => {
      switch (data.kind) {
        case "create_signal":
          setName(source.name ?? "");
          setDescription(source.description ?? "");
          setMetadataSchema(source.metadata_schema ?? { fields: [] });
          break;
        case "update_signal_name":
          setName(source.name ?? "");
          break;
        case "update_signal_description":
          setDescription(source.description ?? "");
          break;
        case "update_signal_metadata":
          setMetadataSchema(source.metadata_schema ?? { fields: [] });
          break;
      }
    },
    [],
  );

  useEffect(() => {
    if (signal && !isFormInitialized) {
      setForm(signal.draft);
      setIsFormInitialized(true);
    }
  }, [signal, isFormInitialized, setForm]);

  // Until a test is explicitly configured, tests follow the form's current
  // playbook scope. Memoized because the config modal re-seeds its local state
  // whenever this object's identity changes.
  const effectiveTestFilters: TestSignalFiltersUi = useMemo(
    () =>
      lastRunFilters ?? {
        playbookScope,
        teammateGroupIds: null,
        dateRange: defaultDateRange,
      },
    [lastRunFilters, playbookScope],
  );

  // The form as the server would store it. Compared against the persisted
  // draft on the same terms, so a trailing space never reads as a change.
  const draftPayload = useMemo<SignalPayload>(
    () => ({
      name: name.trim(),
      description: description.trim(),
      metadata_schema: cleanMetadataSchema(metadataSchema),
      playbook_scope: playbookScope,
    }),
    [name, description, metadataSchema, playbookScope],
  );
  const isFormValid =
    draftPayload.name !== "" && draftPayload.description !== "";
  const hasUnsavedChanges = isSignalFormDirty(
    signal?.draft,
    draftPayload.name,
    draftPayload.description,
    draftPayload.metadata_schema ?? { fields: [] },
    draftPayload.playbook_scope,
  );
  const hasUnpublishedChanges =
    hasUnsavedChanges ||
    (signal !== undefined && checkSignalHasUnpublishedChanges(signal));

  const runTestWithFilters = useCallback(
    async (filters: TestSignalFiltersUi) => {
      if (!name.trim() || !description.trim()) return;

      setTestResultsState({ status: "loading" });

      const now = DateTime.now();
      const rangeEnd = filters.dateRange.end ?? now;
      const rangeStart = rangeEnd.minus(filters.dateRange.length);

      try {
        const data = await testSignalProposal({
          ...(signalId ? { signalId } : {}),
          signal: {
            name,
            description,
            metadata_schema: cleanMetadataSchema(metadataSchema),
            playbook_scope: playbookScope,
          },
          filters: {
            playbookScope: filters.playbookScope,
            ...(filters.teammateGroupIds
              ? { teammateGroupIds: filters.teammateGroupIds }
              : {}),
            lastRecordedAtRange: {
              after: rangeStart.startOf("day").toUTC().toISO(),
              before: rangeEnd.endOf("day").toUTC().toISO(),
            },
          },
        }).unwrap();

        setTestResultsState({
          status: "complete",
          data,
          completedAt: DateTime.now(),
        });
      } catch (err) {
        console.error("Failed to test signal:", err);
        setTestResultsState({ status: "error" });
      }
    },
    [
      signalId,
      name,
      description,
      metadataSchema,
      playbookScope,
      testSignalProposal,
    ],
  );

  const handleRunTestFromModal = useCallback(
    (filters: TestSignalFiltersUi) => {
      setIsTestConfigOpen(false);
      setLastRunFilters(filters);
      runTestWithFilters(filters);
    },
    [runTestWithFilters],
  );

  // Open config modal if filters have not been configured yet
  const handleTestSignalClick = useCallback(() => {
    if (lastRunFilters) {
      runTestWithFilters(lastRunFilters);
    } else {
      setIsTestConfigOpen(true);
    }
  }, [lastRunFilters, runTestWithFilters]);

  const handleConfigureTestClick = useCallback(() => {
    setIsTestConfigOpen(true);
  }, []);

  // One-shot deep link from an Ask Alpharun proposal card (?proposalId=...):
  // the proposal's part is laid over the form as a read-only preview and its
  // evidence becomes the test result. Autosave is held while the proposal is
  // under review, so the preview is never written; Apply saves the form and
  // resolves the proposal, Discard restores the persisted draft. A proposal
  // for another signal loads nothing. While the link is unresolved the form is
  // held: no banner, no publish.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkProposalId = copilotProposalIdSchema.safeParse(
    searchParams.get(SIGNAL_PROPOSAL_QUERY_PARAM),
  ).data;
  const {
    currentData: deepLinkProposal,
    isError: isDeepLinkProposalUnavailable,
  } = useGetPendingSignalProposalQuery(deepLinkProposalId ?? skipToken);
  const isLoadingProposal = deepLinkProposalId !== undefined;
  const clearDeepLinkProposalId = useCallback(() => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(SIGNAL_PROPOSAL_QUERY_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);
  const [proposal, setProposal] = useState<CopilotProposal<"signal"> | null>(
    null,
  );
  useEffect(() => {
    if (deepLinkProposalId && isDeepLinkProposalUnavailable) {
      clearDeepLinkProposalId();
      setError("This suggestion is no longer available.");
    }
  }, [
    deepLinkProposalId,
    isDeepLinkProposalUnavailable,
    clearDeepLinkProposalId,
  ]);
  useEffect(() => {
    if (
      !isFormInitialized ||
      !deepLinkProposalId ||
      !deepLinkProposal ||
      deepLinkProposal.id !== deepLinkProposalId
    ) {
      return;
    }
    clearDeepLinkProposalId();
    const { data } = deepLinkProposal;
    const targetsThisPage =
      data.kind === "create_signal"
        ? isNew
        : deepLinkProposal.resource_id === signalId;
    if (!targetsThisPage) {
      return;
    }
    setTestResultsState({ status: "idle" });
    setError(null);
    layProposalOverForm(data, data);
    if ("evidence" in data) {
      setTestResultsState({
        status: "complete",
        data: data.evidence,
        completedAt: DateTime.fromISO(deepLinkProposal.created_at),
      });
    }
    setProposal(deepLinkProposal);
  }, [
    isFormInitialized,
    deepLinkProposalId,
    deepLinkProposal,
    isNew,
    signalId,
    clearDeepLinkProposalId,
    layProposalOverForm,
  ]);

  const isBusy =
    isLoadingProposal || isResolvingProposal || isPublishing || isReverting;

  // Autosave writes the draft. A new signal is created on its first save;
  // created from a proposal, it takes the id the proposal minted.
  const {
    flushPendingSave,
    saveStatus: autosaveStatus,
    lastSavedAt,
  } = useAutosaveDraft({
    hasChanges: hasUnsavedChanges && isFormValid,
    canAutosave: isFormInitialized && proposal === null && !isBusy,
    onSave: async () => {
      const targetSignalId = signalId ?? createdSignalIdRef.current;
      if (targetSignalId) {
        await saveSignalDraft({ id: targetSignalId, ...draftPayload }).unwrap();
        return;
      }
      const created = await createSignal({
        ...draftPayload,
        ...(proposal ? { proposal_id: proposal.id } : {}),
      }).unwrap();
      createdSignalIdRef.current = created.id;
      setCreatedSignalId(created.id);
    },
  });
  // Ask Alpharun's tools read the stored draft, so a pending edit is written
  // before a message goes out.
  const registerBeforeSend = useResearchAgentProps()?.registerBeforeSend;
  useEffect(
    () => registerBeforeSend?.(flushPendingSave),
    [registerBeforeSend, flushPendingSave],
  );

  const saveStatus = useDraftSaveStatus({
    hasUnpublishedChanges,
    isSaving: autosaveStatus === "saving",
    lastSavedAt,
  });

  if (!isNew && (isLoadingSignal || !isFormInitialized)) {
    return (
      <div className="flex flex-col">
        <PageHeader width={PageWidth.Form} isLoading />
      </div>
    );
  }

  if (!isNew && (isError || !signal)) {
    return (
      <div className="flex flex-col">
        <PageHeader width={PageWidth.Form} title="Signal not found" />
      </div>
    );
  }

  /** Drop the previewed proposal, returning the form to the stored draft. */
  const restoreStoredDraft = () => {
    if (proposal) {
      layProposalOverForm(proposal.data, signal?.draft ?? {});
    }
    setTestResultsState({ status: "idle" });
    setProposal(null);
  };

  // A previewed proposal is not in the draft yet; it is applied or discarded
  // before anything publishes.
  const handlePublish = async () => {
    if (!isFormValid || isBusy || proposal || !hasUnpublishedChanges) return;
    setError(null);
    try {
      if (!(await flushPendingSave())) {
        setError("Your changes could not be saved. Please try again.");
        return;
      }
      const targetSignalId = signalId ?? createdSignalIdRef.current;
      if (!targetSignalId) {
        return;
      }
      await publishSignal(targetSignalId).unwrap();
      onClose();
    } catch (err: any) {
      console.error("Failed to publish signal:", err);
      setError(
        err.data?.message || "Failed to publish signal. Please try again.",
      );
    }
  };

  // A save still in flight would land after the revert and revive the
  // discarded draft, so it is waited for first.
  const handleRevert = async () => {
    if (isBusy) {
      return;
    }
    setError(null);
    await flushPendingSave();
    const targetSignalId = signalId ?? createdSignalIdRef.current;
    if (!targetSignalId) {
      setForm(EMPTY_FORM);
      return;
    }
    try {
      const reverted = await revertSignal(targetSignalId).unwrap();
      if (!reverted) {
        onClose();
        return;
      }
      setForm(reverted.draft);
    } catch (err: any) {
      console.error("Failed to discard signal changes:", err);
      setError(
        err.data?.message || "Failed to discard changes. Please try again.",
      );
    }
  };

  // The form already shows the proposal, so accepting saves the form as it is.
  // The proposal is resolved first: one that is no longer pending answers 409,
  // and its preview must not reach the draft.
  const handleApplyProposal = async () => {
    if (!proposal || isBusy) return;
    setError(null);
    try {
      await resolveProposal({
        proposalId: proposal.id,
        status: "applied",
      }).unwrap();
    } catch (err: any) {
      console.error("Failed to accept the suggestion:", err);
      const isGone = err.status === 409;
      setError(
        isGone
          ? "This suggestion is no longer available."
          : "Failed to accept the suggestion. Please try again.",
      );
      if (isGone) {
        restoreStoredDraft();
      }
      return;
    }
    setProposal(null);
    if (!(await flushPendingSave())) {
      setError("Your changes could not be saved. Please try again.");
    }
  };

  const handleDiscardProposal = async () => {
    if (!proposal || isBusy) return;
    setError(null);
    try {
      await resolveProposal({
        proposalId: proposal.id,
        status: "discarded",
      }).unwrap();
    } catch (err: any) {
      // A proposal that has moved on is already not being applied, so the
      // preview is dropped either way.
      if (err.status !== 409) {
        console.error("Failed to discard the suggestion:", err);
        setError("Failed to discard the suggestion. Please try again.");
        return;
      }
      setError("This suggestion is no longer available.");
    }
    restoreStoredDraft();
  };

  const testResultPillState =
    testResultsState.status === "idle" ? null : testResultsState;

  return (
    <>
      <div className="flex h-full flex-col">
        <SignalEditorContainer
          name={name}
          description={description}
          metadataSchema={metadataSchema}
          playbookScope={playbookScope}
          error={
            error ??
            (autosaveStatus === "error"
              ? "Your latest changes could not be saved."
              : null)
          }
          onNameChange={setName}
          onDescriptionChange={setDescription}
          onMetadataSchemaChange={setMetadataSchema}
          onPlaybookScopeChange={setPlaybookScope}
          onClose={onClose}
          onDiscard={proposal ? handleDiscardProposal : handleRevert}
          onSubmit={handlePublish}
          isLoading={isPublishing}
          isSubmitDisabled={
            isBusy ||
            proposal !== null ||
            !isFormValid ||
            !hasUnpublishedChanges
          }
          submitLabel="Publish"
          showDiscard={hasUnpublishedChanges && !isBusy && proposal === null}
          hasUnpublishedChanges={signal !== undefined && hasUnpublishedChanges}
          onTestSignalClick={handleTestSignalClick}
          onConfigureTestClick={handleConfigureTestClick}
          isTestSignalDisabled={
            !isFormValid || testResultsState.status === "loading"
          }
          testResultPillState={testResultPillState}
          onViewMatches={() => setIsMatchesModalOpen(true)}
          saveStatus={saveStatus}
          // Under a proposal the diff reads draft → proposed; otherwise
          // unpublished draft → live.
          diffBaseline={proposal ? signal?.draft : (signal?.live ?? undefined)}
          suggestion={
            proposal && !isLoadingProposal
              ? {
                  isNewSignal: isNew,
                  onDiscard: handleDiscardProposal,
                  onApply: handleApplyProposal,
                  isResolving: isResolvingProposal,
                }
              : undefined
          }
        />
      </div>

      <SignalTestConfigModal
        isOpen={isTestConfigOpen}
        onClose={() => setIsTestConfigOpen(false)}
        initialFilters={effectiveTestFilters}
        onRunTest={handleRunTestFromModal}
      />

      {testResultsState.status === "complete" ? (
        <SignalTestMatchesModal
          isOpen={isMatchesModalOpen}
          onClose={() => setIsMatchesModalOpen(false)}
          data={testResultsState.data}
          metadataSchema={metadataSchema}
        />
      ) : null}
    </>
  );
};
